import { useState, useRef, AllHTMLAttributes } from 'react';
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useHover,
  useFocus,
  useDismiss,
  useRole,
  useInteractions,
  arrow,
  Placement,
} from '@floating-ui/react';

export type UseTooltipOptions = {
  placement: Placement;
};

// use tooltip calls floating UI  and renders a tooltip in an accessible manner
export const useTooltip = ({ placement }: UseTooltipOptions) => {
  const [isOpen, setIsOpen] = useState(false);
  const arrowRef = useRef<SVGSVGElement>(null);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement,
    whileElementsMounted: autoUpdate,
    middleware: [
      //   The offset modifier is used to apply a gap between the  element and its anchor element.
      //  Gives the ability shift the floating element along the x and y axes from its default position.
      // mainAxis =  vertical distance to move the floating element (positive for down, negative for up).
      // crossAxis = horizontal distance to move the floating element (positive for right, negative for left).
      offset({ mainAxis: 0, crossAxis: -4 }),
      flip({
        fallbackAxisSideDirection: 'start',
      }),
      // Shift does not accept fixed x and y values like offset. Instead, it dynamically adjusts the element based on available space.
      //https://floating-ui.com/docs/shift
      // this gives the tooltip 8px of allover padding from the anchor element
      shift({ padding: 8 }),
      arrow({
        element: arrowRef,
      }),
    ],
  });

  const hover = useHover(context, { move: false });
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'tooltip' });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    focus,
    dismiss,
    role,
  ]);

  return {
    getTargetProps: (userOverrides: AllHTMLAttributes<HTMLElement> = {}) => {
      return {
        ...getReferenceProps({
          ...userOverrides,
        }),
        ref: refs.setReference,
      };
    },
    getTooltipProps: (userOverrides: AllHTMLAttributes<HTMLElement> = {}) => {
      return {
        ...getFloatingProps({
          ...userOverrides,
          style: {
            ...floatingStyles,
            ...userOverrides.style,
          },
        }),
        ref: refs.setFloating,
        arrowRef,
        context,
      };
    },
  };
};
